import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../config/environment';
import { getPrimaryProductImageUrl } from '../utils/productImages';
import { wishlistService } from '../services/api';

const LikedItemsContext = createContext();

export const useLikedItems = () => {
  const context = useContext(LikedItemsContext);
  if (!context) {
    throw new Error('useLikedItems must be used within a LikedItemsProvider');
  }
  return context;
};

function mapApiWishlistEntryToLikedItem(item) {
  const product = item?.product && typeof item.product === 'object' ? item.product : null;
  const productId =
    product?._id ||
    product?.id ||
    item?.productId ||
    item?.product?._id ||
    item?._id ||
    item?.id;
  if (productId == null || String(productId).trim() === '') return null;
  const id = String(productId);
  const name = product?.name || item?.name || 'Product';
  const primary = product ? getPrimaryProductImageUrl(product) : null;
  let image = primary;
  if (!image && item?.image) {
    image = typeof item.image === 'string' ? item.image : item.image?.url;
  }
  return {
    id,
    name,
    price: product?.price ?? item?.price ?? 0,
    originalPrice: product?.originalPrice ?? item?.originalPrice ?? product?.price ?? item?.price ?? 0,
    image,
    category: product?.category || item?.category,
    subcategory: product?.subcategory || item?.subcategory,
    url: product?.url || item?.url,
    isNew: product?.isNew ?? item?.isNew,
    isOnSale: product?.isOnSale ?? item?.isOnSale,
    dateAdded:
      item?.addedAt || item?.createdAt || item?.dateAdded || new Date().toISOString(),
  };
}

/** @returns {boolean|null} `true`/`false` when the check payload includes a flag; `null` if unknown / not successful. */
function parseWishlistCheckResponse(chk) {
  if (!chk || chk.success !== true) return null;
  const d = chk.data;
  if (d && typeof d.isInWishlist === 'boolean') return d.isInWishlist;
  if (d?.data != null && typeof d.data.isInWishlist === 'boolean') return d.data.isInWishlist;
  return null;
}

export const LikedItemsProvider = ({ children }) => {
  const { user } = useAuth();
  const [likedItems, setLikedItems] = useState([]);
  const [likedProductIds, setLikedProductIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /** Latest items for merging after GET /wishlist (avoid stale closure). */
  const likedItemsRef = useRef([]);
  useEffect(() => {
    likedItemsRef.current = likedItems;
  }, [likedItems]);

  useEffect(() => {
    const savedLikedItems = localStorage.getItem('likedItems');
    if (savedLikedItems) {
      try {
        const parsedItems = JSON.parse(savedLikedItems);
        const normalized = parsedItems
          .map((item) => ({ ...item, id: String(item.id) }))
          .filter((item) => item.id);
        setLikedItems(normalized);
        setLikedProductIds(new Set(normalized.map((item) => item.id)));
      } catch (e) {
        console.error('Error parsing liked items from localStorage:', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('likedItems', JSON.stringify(likedItems));
  }, [likedItems]);

  const testBackendConnectivity = async () => {
    try {
      const response = await fetch(getApiUrl('/health'));
      return response.ok;
    } catch (err) {
      console.error('Backend connectivity test failed:', err);
      return false;
    }
  };

  const loadLikedItemsFromBackend = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const isBackendAvailable = await testBackendConnectivity();
      if (!isBackendAvailable) {
        return;
      }
      const response = await wishlistService.getWishlist();
      const data = response?.data;
      // Only replace/merge when the contract includes `data.items` (array). Otherwise the
      // server may return success without `items` and we must not wipe localStorage / UI.
      if (response?.success && data && Array.isArray(data.items)) {
        const backendItems = data.items.map(mapApiWishlistEntryToLikedItem).filter(Boolean);
        const backendIds = new Set(backendItems.map((i) => String(i.id)));
        const prev = likedItemsRef.current || [];
        const orphans = prev.filter((p) => p?.id && !backendIds.has(String(p.id)));
        const merged = [...backendItems, ...orphans];
        setLikedItems(merged);
        setLikedProductIds(new Set(merged.map((i) => String(i.id))));
      }
    } catch (err) {
      console.error('Error loading wishlist from backend:', err);
      setError(err?.message || 'wishlist_load_failed');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadLikedItemsFromBackend();
  }, [loadLikedItemsFromBackend]);

  const addToLikedItems = async (product) => {
    const pid = String(product?.id || product?._id || '').trim();
    if (!pid) return false;

    const newLikedItem = {
      id: pid,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: getPrimaryProductImageUrl(product),
      category: product.category,
      subcategory: product.subcategory,
      url: product.url,
      isNew: product.isNew,
      isOnSale: product.isOnSale,
      dateAdded: new Date().toISOString(),
    };

    let shouldAdd = false;
    setLikedItems((prev) => {
      if (prev.some((i) => String(i.id) === pid)) return prev;
      shouldAdd = true;
      return [...prev, newLikedItem];
    });
    if (!shouldAdd) return false;

    setLikedProductIds((prev) => new Set([...prev, pid]));

    if (user) {
      try {
        const isBackendAvailable = await testBackendConnectivity();
        if (isBackendAvailable) {
          let response = { success: true };
          try {
            const chk = await wishlistService.checkInWishlist(pid);
            const onServer = parseWishlistCheckResponse(chk);
            if (onServer === true) {
              /* Already on server; POST /wishlist/items would toggle-remove — skip. */
            } else if (onServer === false) {
              response = await wishlistService.addWishlistItemOrToggle(pid);
            } else {
              response = await wishlistService.addToWishlist(pid);
            }
          } catch (_) {
            response = await wishlistService.addToWishlist(pid);
          }
          if (!response?.success) {
            console.warn('Wishlist add API:', response?.error || response?.message);
          }
        }
      } catch (e) {
        console.warn('Wishlist add API error:', e);
      }
    }

    return true;
  };

  const removeFromLikedItems = async (productId) => {
    const pid = String(productId);
    if (user) {
      try {
        const isBackendAvailable = await testBackendConnectivity();
        if (isBackendAvailable) {
          await wishlistService.removeWishlistItemLine(pid);
        }
      } catch (e) {
        console.warn('Wishlist remove API error:', e);
      }
    }
    setLikedItems((prev) => prev.filter((item) => item.id !== pid));
    setLikedProductIds((prev) => {
      const next = new Set(prev);
      next.delete(pid);
      return next;
    });
  };

  const isLiked = (productId) => likedProductIds.has(String(productId));

  const clearLikedItems = async () => {
    if (user) {
      try {
        const isBackendAvailable = await testBackendConnectivity();
        if (isBackendAvailable) {
          await wishlistService.clearWishlist();
        }
      } catch (e) {
        console.warn('Wishlist clear API error:', e);
      }
    }
    setLikedItems([]);
    setLikedProductIds(new Set());
  };

  const getLikedItemsCount = () => likedItems.length;

  const getMostLikedProducts = (limit = 8) =>
    likedItems.slice(0, limit).map((item) => ({
      productId: item.id,
      name: item.name,
      image: item.image,
      price: item.price,
      category: item.category,
    }));

  const forceSyncLikedItems = async () => {
    if (!user) return false;
    await loadLikedItemsFromBackend();
    return true;
  };

  const syncAllLikedItemsToBackend = async () => {
    if (!user) return false;
    try {
      const isBackendAvailable = await testBackendConnectivity();
      if (!isBackendAvailable) return false;
      for (const item of likedItems) {
        try {
          await wishlistService.addToWishlist(item.id);
        } catch (e) {
          console.warn('Wishlist sync item failed:', item.id, e);
        }
      }
      return true;
    } catch (e) {
      console.error('Manual wishlist sync failed:', e);
      return false;
    }
  };

  const value = {
    likedItems,
    addToLikedItems,
    removeFromLikedItems,
    isLiked,
    clearLikedItems,
    getLikedItemsCount,
    getMostLikedProducts,
    loadLikedItemsFromBackend,
    forceSyncLikedItems,
    syncAllLikedItemsToBackend,
    loading,
    error,
  };

  return <LikedItemsContext.Provider value={value}>{children}</LikedItemsContext.Provider>;
};
